import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

const Frame = styled.iframe`
  width: 100%;
  border: none;
  margin-top: var(--spacing-03);
  display: block;
  overflow: hidden;
`;

const wrapHtml = (html: string) => `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <base target="_blank" />
    <style>
      html, body {
        margin: 0;
        padding: 0;
      }
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        line-height: 1.5;
        color: #1a1a1a;
        word-break: break-word;
      }
      img {
        max-width: 100%;
        height: auto;
      }
      a {
        color: #5b6bff;
      }
    </style>
  </head>
  <body>${html}</body>
</html>`;

// Don't wait forever on a hung/blocked image before revealing the content.
const IMAGE_SETTLE_TIMEOUT_MS = 3000;

interface Props {
  /**
   * Raw email HTML content from the `htmlContent` key on the API payload.
   */
  html: string;
  /** Called once the content (including all images) has settled. */
  onReady?: () => void;
  /** Called when the user clicks anywhere inside the iframe content. */
  onIframeClick?: () => void;
}

/**
 * Renders untrusted email HTML inside a sandboxed iframe via `srcDoc`.
 *
 * The frame is sandboxed with `allow-same-origin` (so the parent can read its
 * content height and auto-resize) while omitting `allow-scripts` — the email
 * HTML can never run JavaScript. `allow-popups` +
 * `allow-popups-to-escape-sandbox` let links open in a new (un-sandboxed) tab;
 * without them a sandboxed frame silently blocks `target="_blank"`.
 *
 * To avoid a visible staged resize, the frame stays collapsed and hidden until
 * its content has fully settled (all images loaded), then it's measured once
 * and revealed at its final height in a single paint.
 */
export const EmailHtmlContent: FC<Props> = ({
  html,
  onReady,
  onIframeClick,
}) => {
  const ref = useRef<HTMLIFrameElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const imageCleanupRef = useRef<(() => void) | null>(null);
  const iframeClickCleanupRef = useRef<(() => void) | null>(null);
  const onReadyRef = useRef(onReady);
  const onIframeClickRef = useRef(onIframeClick);

  onReadyRef.current = onReady;
  onIframeClickRef.current = onIframeClick;
  const [height, setHeight] = useState<number>(0);
  const [ready, setReady] = useState(false);

  const srcDoc = useMemo(() => wrapHtml(html), [html]);

  // Re-hide and re-measure whenever the content changes. Cancel any pending
  // settle timer and remove image/iframe-click listeners from the previous load.
  useEffect(() => {
    imageCleanupRef.current?.();
    imageCleanupRef.current = null;
    iframeClickCleanupRef.current?.();
    iframeClickCleanupRef.current = null;
    clearTimeout(settleTimerRef.current);
    settleTimerRef.current = undefined;
    setReady(false);
    setHeight(0);
  }, [srcDoc]);

  const measure = useCallback(() => {
    const doc = ref.current?.contentDocument;

    if (doc?.body) {
      setHeight(doc.documentElement.scrollHeight || doc.body.scrollHeight);
    }
  }, []);

  const handleLoad = useCallback(() => {
    const doc = ref.current?.contentDocument;
    const body = doc?.body;

    if (!doc || !body) {
      setReady(true);
      onReadyRef.current?.();

      return;
    }

    // The frame is same-origin (srcDoc + allow-same-origin), so we can fix up
    // links directly — no postMessage bridge needed. Force every link to open
    // in a new tab (overriding any target the source set, e.g. Gmail's
    // target="_blank") so clicks never navigate away inside the iframe.
    doc.querySelectorAll('a[href]').forEach((anchor) => {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    });

    let finalized = false;

    const finalize = () => {
      if (finalized) {
        return;
      }
      finalized = true;
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = undefined;

      // Measure the fully-loaded content once, then reveal at final height.
      measure();
      setReady(true);
      onReadyRef.current?.();

      // Keep observing for later genuine reflows (rare once images are in).
      observerRef.current?.disconnect();
      const observer = new ResizeObserver(() => measure());

      observer.observe(body);
      observerRef.current = observer;

      // Clicks inside the iframe don't bubble to the parent document, so
      // popovers anchored in the parent never receive an outside-click signal.
      // Forwarding mousedown from the iframe's document closes them correctly.
      iframeClickCleanupRef.current?.();
      const handleIframeMouseDown = () => onIframeClickRef.current?.();

      doc.addEventListener('mousedown', handleIframeMouseDown);
      iframeClickCleanupRef.current = () =>
        doc.removeEventListener('mousedown', handleIframeMouseDown);
    };

    const pendingImages = Array.from(doc.images).filter((img) => !img.complete);

    if (pendingImages.length === 0) {
      finalize();

      return;
    }

    let remaining = pendingImages.length;
    const onSettle = () => {
      remaining -= 1;

      if (remaining <= 0) {
        finalize();
      }
    };

    pendingImages.forEach((img) => {
      img.addEventListener('load', onSettle);
      img.addEventListener('error', onSettle);
    });

    imageCleanupRef.current = () => {
      pendingImages.forEach((img) => {
        img.removeEventListener('load', onSettle);
        img.removeEventListener('error', onSettle);
      });
    };

    settleTimerRef.current = setTimeout(finalize, IMAGE_SETTLE_TIMEOUT_MS);
  }, [measure]);

  useEffect(() => {
    return () => {
      clearTimeout(settleTimerRef.current);
      observerRef.current?.disconnect();
      imageCleanupRef.current?.();
      iframeClickCleanupRef.current?.();
    };
  }, []);

  return (
    <Frame
      ref={ref}
      title="Email content"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcDoc}
      onLoad={handleLoad}
      style={{
        height: ready && height ? `${height}px` : 0,
        visibility: ready ? 'visible' : 'hidden',
      }}
    />
  );
};
